import { Link } from "react-router-dom";
import { MessageCircle, MapPin, Clock, Store, ShoppingBag, Music2 } from "lucide-react";
import { mediaUrl, logoTerangUrl } from "../../lib/media";
import { useChat } from "../../chat/chatKonteks";

function Tautan({ t }) {
  const url = t.url || "#";
  const cls = "block text-sm text-[#AAAAAA] leading-9 hover:text-white transition-colors";
  if (url.startsWith("/")) return <Link to={url} className={cls}>{t.teks}</Link>;
  return <a href={url} target="_blank" rel="noopener noreferrer" className={cls}>{t.teks}</a>;
}

function KolomTautan({ col, tid }) {
  if (!col) return <div />;
  return (
    <div data-testid={tid}>
      <div className="text-[13px] font-medium text-white mb-3">{col.judul}</div>
      {(col.tautan || []).map((t, j) => <Tautan key={j} t={t} />)}
    </div>
  );
}

const SOSIAL = [
  { nama: "Tokopedia", url: "https://www.tokopedia.com/hoonstore", Ikon: Store },
  { nama: "Shopee", url: "https://shopee.co.id/hoon.store", Ikon: ShoppingBag },
  { nama: "TikTok", url: "https://www.tiktok.com/@hoonstore", Ikon: Music2 },
];

function Sosial() {
  return (
    <div className="mt-3 flex items-center gap-2" data-testid="footer-sosial">
      {SOSIAL.map(({ nama, url, Ikon }) => (
        <a
          key={nama}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title={nama}
          aria-label={nama}
          data-testid={`footer-sosial-${nama.toLowerCase()}`}
          className="flex h-9 w-9 items-center justify-center border border-[#333336] text-[#AAAAAA] transition-colors hover:border-[#FF6B1A] hover:text-white"
          style={{ borderRadius: 2 }}
        >
          <Ikon size={16} strokeWidth={1.6} />
        </a>
      ))}
    </div>
  );
}

function Kontak({ konten }) {
  const wa = (konten?.kontak_wa_teks || "").trim();
  const waUrl = (konten?.kontak_wa_url || "").trim();
  const area = (konten?.kontak_area || "").trim();
  const jam = (konten?.kontak_jam || "").trim();
  const chat = useChat();
  const baris = "flex items-start gap-3 text-sm text-[#AAAAAA] leading-9";
  return (
    <div data-testid="footer-kontak">
      <div className="text-[13px] font-medium text-white mb-3">Kontak</div>
      {chat.aktif ? (
        <button type="button" onClick={() => chat.buka(null)} className={`${baris} hover:text-white transition-colors bg-transparent border-0 p-0 cursor-pointer text-left`} data-testid="footer-kontak-wa" data-kontak="chat">
          <MessageCircle size={16} strokeWidth={1.6} className="shrink-0 mt-[10px] text-[#FF6B1A]" />
          <span>Chat dengan Kami</span>
        </button>
      ) : wa && (
        <a href={waUrl || "#"} target="_blank" rel="noopener noreferrer" className={`${baris} hover:text-white transition-colors`} data-testid="footer-kontak-wa">
          <MessageCircle size={16} strokeWidth={1.6} className="shrink-0 mt-[10px] text-[#FF6B1A]" />
          <span>{wa}</span>
        </a>
      )}
      {area && (
        <div className={baris} data-testid="footer-kontak-area">
          <MapPin size={16} strokeWidth={1.6} className="shrink-0 mt-[10px] text-[#FF6B1A]" />
          <span>{area}</span>
        </div>
      )}
      {jam && (
        <div className={baris} data-testid="footer-kontak-jam">
          <Clock size={16} strokeWidth={1.6} className="shrink-0 mt-[10px] text-[#FF6B1A]" />
          <span>{jam}</span>
        </div>
      )}
      <Sosial />
    </div>
  );
}

// KEPUTUSAN 22/10/2026 no. 6 + INSTRUKSI 01/11/2026 TAMBAHAN-1 butir 1 (syarat verifikasi brand Google): baris paling bawah footer komputer =
// "Syarat dan Ketentuan" · "Kebijakan Privasi" (footer HP punya tautannya sendiri di FooterHp.jsx, tidak diubah)
const kelasTautan = "text-[13px] text-[#8E8E93] hover:text-white underline-offset-4 hover:underline";
const TautanBawah = ({ tid }) => (
  <span className="inline-flex flex-wrap gap-x-4 gap-y-1">
    <Link to="/syarat-ketentuan" data-testid={tid} className={kelasTautan}>Syarat dan Ketentuan</Link>
    <Link to="/kebijakan-privasi" data-testid={`${tid}-privasi`} className={kelasTautan}>Kebijakan Privasi</Link>
  </span>
);

export function SiteFooter({ konten, settings }) {
  const namaToko = settings?.nama_toko || "HoonStore";
  const kolom = konten?.footer_kolom || [];
  const tahun = new Date().getFullYear();
  const copyright = konten?.footer_copyright || `© ${tahun} ${namaToko}. Hak cipta dilindungi.`;

  const brand = (ukuran, tinggiLogo) => (
    <div>
      <Link to="/" data-testid="footer-logo-link" className="inline-block">
        <img src={logoTerangUrl(settings?.logo_terang_url)} alt="HoonStore" data-testid="footer-logo-img"
          width="600" height="80" loading="lazy" decoding="async"
          className="w-auto block" style={{ height: tinggiLogo }} />
      </Link>
      {konten?.footer_deskripsi && (
        <p className="mt-4 text-sm leading-[1.7] text-[#AAAAAA] max-w-[320px]">{konten.footer_deskripsi}</p>
      )}
    </div>
  );

  return (
    <footer className="hsv5-on-dark bg-[#1D1D1F] text-[#AAAAAA] mt-6 md:mt-8" data-testid="site-footer">
      {/* Desktop */}
      <div className="hidden md:block">
        <div className="mx-auto w-full max-w-[1440px] px-20 pt-16 pb-14">
          <div className="mx-auto w-full max-w-[1280px] grid gap-12" style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr" }}>
            {brand("text-[22px]", 28)}
            <KolomTautan col={kolom[0]} tid="footer-column-0" />
            <KolomTautan col={kolom[1]} tid="footer-column-1" />
            <Kontak konten={konten} />
          </div>
        </div>
        {/* PR18 (KEPUTUSAN 22/10 footer no. 6): tautan kecil di baris paling bawah; tiga kolom tidak berubah */}
        <div className="border-t border-[#333336]">
          <div className="mx-auto w-full max-w-[1440px] px-20 py-[22px] flex items-center justify-between gap-6 text-[13px] text-[#8E8E93]">
            <div className="text-left" data-testid="footer-copyright">{copyright}</div>
            <TautanBawah tid="footer-syarat" />
          </div>
        </div>
      </div>
      {/* Mobile */}
      <div className="md:hidden px-4 pt-12 pb-6">
        <div className="flex flex-col gap-8">
          {brand("text-[20px]", 24)}
          {(kolom[0] || kolom[1]) && (
            <div className="grid grid-cols-2 gap-6 [&_a]:leading-10">
              <KolomTautan col={kolom[0]} tid="footer-column-m-0" />
              <KolomTautan col={kolom[1]} tid="footer-column-m-1" />
            </div>
          )}
          <div className="[&_a]:leading-10">
            <Kontak konten={konten} />
          </div>
        </div>
        <div className="border-t border-[#333336] mt-8 pt-5 text-[13px] text-[#8E8E93]">
          <div data-testid="footer-copyright-m">{copyright}</div>
          <div className="mt-3"><TautanBawah tid="footer-syarat-m" /></div>
        </div>
      </div>
    </footer>
  );
}
